// saseService.js
/**
 * SASE Security Service
 * API service for SASE Security features at organization and device levels
 *
 * Created: Dec 2025
 * Copyright (c) 2025, NETGEAR, Inc.
 */

import axiosInstance from './axiosInstance';
import apiService from '../hooks/useApi';
import Cookies from 'universal-cookie';

const cookies = new Cookies();
const API_BASE_URL = process.env.apiHost;

/**
 * Helper to get organization ID from various sources
 * Follows MUI lite pattern from OrganizationContext
 * Priority: localStorage OrgId > currentOrg > cookies > selectedType object
 */
const getOrgId = () => {
  // Try localStorage direct keys first (MUI lite pattern from OrganizationContext)
  const lsOrgId = localStorage.getItem('OrgId');
  if (lsOrgId && lsOrgId !== 'null' && lsOrgId !== 'undefined') return lsOrgId;

  const lsCurrentOrg = localStorage.getItem('currentOrg');
  if (lsCurrentOrg && lsCurrentOrg !== 'null' && lsCurrentOrg !== 'undefined') return lsCurrentOrg;

  // Try other localStorage keys
  const directOrgId = localStorage.getItem('organization_Id') || localStorage.getItem('Organization_Id');
  if (directOrgId && directOrgId !== 'null' && directOrgId !== 'undefined') return directOrgId;

  // Try cookies (matches original imuserportal)
  const cookieOrgId = cookies.get('organization_Id');
  if (cookieOrgId) return cookieOrgId;

  // Try selectedType object (MUI lite pattern)
  try {
    const selectedType = JSON.parse(localStorage.getItem('selectedType'));
    // Check for orgId property first
    if (selectedType?.orgId) return selectedType.orgId;
    // For organization-level pages, the id might be the org ID when type is Organization
    if (selectedType?.type === 'Organization' && selectedType?.id) return selectedType.id;
  } catch (e) {
    // Ignore parse errors
  }

  return null;
};

/**
 * Helper to get common IDs from cookies/localStorage
 */
const getCommonIds = () => {
  const orgId = getOrgId();
  const networkId = localStorage.getItem('currentNetwork') || localStorage.getItem('networkId') || cookies.get('currentNetwork');

  // Debug logging (can be removed in production)
  if (!orgId) {
    console.warn('SASE API: orgId is null. Available localStorage keys:', {
      OrgId: localStorage.getItem('OrgId'),
      currentOrg: localStorage.getItem('currentOrg'),
      organization_Id: localStorage.getItem('organization_Id'),
      selectedType: localStorage.getItem('selectedType')
    });
  }

  return {
    _Id: cookies.get('_Id'),
    orgId,
    networkId,
    accountId: cookies.get('accountId')
  };
};

// ==============================|| ORGANIZATION LEVEL SASE APIs ||============================== //

/**
 * Get SASE Organization by orgId
 * @param {string} [overrideOrgId] - Optional org ID to override the auto-detected one
 */
export const getSaseOrganization = async (overrideOrgId = null) => {
  const { orgId: autoOrgId, _Id } = getCommonIds();
  const orgId = overrideOrgId || autoOrgId;

  if (!orgId) {
    console.error('SASE API: Cannot call getSaseOrganization without orgId');
    return { response: { status: false, message: 'Organization ID not available' } };
  }

  try {
    const response = await axiosInstance.get(`${API_BASE_URL}saseOrganization/v1/get/${_Id}/${orgId}`, {
      headers: {
        token: cookies.get('token') || localStorage.getItem('token'),
        accountId: cookies.get('accountId') || localStorage.getItem('accountId')
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching SASE Organization:', error);
    throw error;
  }
};

/**
 * Get partner usage details (Organization level SASE status)
 * Returns SASE enabled status, trial info, user/gateway counts
 * @param {string} [overrideOrgId] - Optional org ID to override the auto-detected one
 */
export const getPartnerUsage = async (overrideOrgId = null) => {
  const { _Id, orgId: autoOrgId } = getCommonIds();
  const orgId = overrideOrgId || autoOrgId;

  if (!_Id) {
    console.error('SASE API: Cannot call getPartnerUsage without _Id');
    return { response: { status: false, message: 'User ID not available' } };
  }

  if (!orgId) {
    console.error('SASE API: Cannot call getPartnerUsage without orgId');
    return { response: { status: false, message: 'Organization ID not available' } };
  }

  try {
    const response = await axiosInstance.get(`${API_BASE_URL}saseOrganization/v1/getPartnerUsage/${_Id}/${orgId}`, {
      possibleHeaders: ['_Id', 'networkId']
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching partner usage:', error);
    throw error;
  }
};

/**
 * Enable SASE Security at organization level
 * @param {string} [overrideOrgId] - Optional org ID to override the auto-detected one
 */
export const enableSaseSecurity = async (overrideOrgId = null) => {
  const { _Id, orgId: autoOrgId, networkId } = getCommonIds();
  const orgId = overrideOrgId || autoOrgId;

  if (!_Id) {
    console.error('SASE API: Cannot call enableSaseSecurity without _Id');
    return { response: { status: false, message: 'User ID not available' } };
  }

  if (!orgId) {
    console.error('SASE API: Cannot call enableSaseSecurity without orgId');
    return { response: { status: false, message: 'Organization ID not available' } };
  }

  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}saseOrganization/v1/enableSASESecurity/${_Id}/${orgId}`,
      {},
      {
        headers: {
          networkId: networkId
        },
        possibleHeaders: ['_Id']
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error enabling SASE security:', error);
    throw error;
  }
};

/**
 * Disable SASE Security at organization level
 * @param {string} [overrideOrgId] - Optional org ID to override the auto-detected one
 */
export const disableSaseSecurity = async (overrideOrgId = null) => {
  const { _Id, orgId: autoOrgId, networkId } = getCommonIds();
  const orgId = overrideOrgId || autoOrgId;

  if (!_Id) {
    console.error('SASE API: Cannot call disableSaseSecurity without _Id');
    return { response: { status: false, message: 'User ID not available' } };
  }

  if (!orgId) {
    console.error('SASE API: Cannot call disableSaseSecurity without orgId');
    return { response: { status: false, message: 'Organization ID not available' } };
  }

  try {
    const response = await axiosInstance.delete(`${API_BASE_URL}saseOrganization/v1/disableSASESecurity/${_Id}/${orgId}`, {
      headers: {
        networkId: networkId
      },
      possibleHeaders: ['_Id']
    });
    return response.data;
  } catch (error) {
    console.error('Error disabling SASE security:', error);
    throw error;
  }
};

/**
 * Create partner org session (to show security console)
 * Returns redirect URL for security console
 * @param {string} [overrideOrgId] - Optional org ID to override the auto-detected one
 */
export const createPartnerOrgSession = async (overrideOrgId = null) => {
  const { _Id, orgId: autoOrgId } = getCommonIds();
  const orgId = overrideOrgId || autoOrgId;

  if (!_Id) {
    console.error('SASE API: Cannot call createPartnerOrgSession without _Id');
    return { response: { status: false, message: 'User ID not available' } };
  }

  if (!orgId) {
    console.error('SASE API: Cannot call createPartnerOrgSession without orgId');
    return { response: { status: false, message: 'Organization ID not available' } };
  }

  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}saseOrganization/v1/createPartnerOrgSession/${_Id}/${orgId}`,
      {},
      {
        possibleHeaders: ['_Id']
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating partner org session:', error);
    throw error;
  }
};

/**
 * Delete partner org session (hide security console)
 * @param {string} [overrideOrgId] - Optional org ID to override the auto-detected one
 */
export const deletePartnerOrgSession = async (overrideOrgId = null) => {
  const { _Id, orgId: autoOrgId } = getCommonIds();
  const orgId = overrideOrgId || autoOrgId;

  if (!_Id) {
    console.error('SASE API: Cannot call deletePartnerOrgSession without _Id');
    return { response: { status: false, message: 'User ID not available' } };
  }

  if (!orgId) {
    console.error('SASE API: Cannot call deletePartnerOrgSession without orgId');
    return { response: { status: false, message: 'Organization ID not available' } };
  }

  try {
    const response = await axiosInstance.delete(`${API_BASE_URL}saseOrganization/v1/deletePartnerOrgSession/${_Id}/${orgId}`, {
      possibleHeaders: ['_Id']
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting partner org session:', error);
    throw error;
  }
};

// ==============================|| DEVICE LEVEL SASE APIs ||============================== //

/**
 * Get partner usage at device level
 * Returns device-specific SASE status and session info
 * URL: saseDevice/v1/security/status/{_Id}/{networkId}/{deviceId}
 */
export const getPartnerUsageDeviceLevel = async () => {
  const { _Id, networkId } = getCommonIds();
  const deviceId = localStorage.getItem('selectedDeviceId');

  if (!_Id) {
    console.error('SASE API: Cannot call getPartnerUsageDeviceLevel without _Id');
    return { response: { status: false, message: 'User ID not available' } };
  }

  if (!networkId) {
    console.error('SASE API: Cannot call getPartnerUsageDeviceLevel without networkId');
    return { response: { status: false, message: 'Network ID not available' } };
  }

  if (!deviceId) {
    console.error('SASE API: Cannot call getPartnerUsageDeviceLevel without deviceId');
    return { response: { status: false, message: 'Device ID not available' } };
  }

  try {
    const response = await axiosInstance.get(`${API_BASE_URL}saseDevice/v1/security/status/${_Id}/${networkId}/${deviceId}`, {
      possibleHeaders: ['_Id', 'networkId']
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching device level partner usage:', error);
    throw error;
  }
};

/**
 * Check IPSec Site-to-Site conflicts before enabling SASE
 * URL: saseDevice/v1/checkIpSecSites/{_Id}/{networkId}/{deviceId}
 * Returns details: true if conflict exists, false if no conflict
 */
export const checkIpSecSites = async () => {
  const { _Id, networkId } = getCommonIds();
  const deviceId = localStorage.getItem('selectedDeviceId');

  if (!_Id) {
    console.error('SASE API: Cannot call checkIpSecSites without _Id');
    return { response: { status: false, message: 'User ID not available' } };
  }

  if (!networkId) {
    console.error('SASE API: Cannot call checkIpSecSites without networkId');
    return { response: { status: false, message: 'Network ID not available' } };
  }

  if (!deviceId) {
    console.error('SASE API: Cannot call checkIpSecSites without deviceId');
    return { response: { status: false, message: 'Device ID not available' } };
  }

  try {
    const response = await axiosInstance.get(
      `${API_BASE_URL}saseDevice/v1/checkIpSecSites/${_Id}/${networkId}/${deviceId}`,
      {
        possibleHeaders: ['_Id', 'networkId']
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error checking IPSec sites:', error);
    throw error;
  }
};

/**
 * Check gateway subnet for conflicts before enabling SASE
 * URL: saseDevice/v1/partner/organisation/gateway/subnet/{_Id}/{networkId}/{deviceId}
 */
export const checkGatewaySubnet = async () => {
  const { _Id, networkId } = getCommonIds();
  const deviceId = localStorage.getItem('selectedDeviceId');

  if (!_Id) {
    console.error('SASE API: Cannot call checkGatewaySubnet without _Id');
    return { response: { status: false, message: 'User ID not available' } };
  }

  if (!networkId) {
    console.error('SASE API: Cannot call checkGatewaySubnet without networkId');
    return { response: { status: false, message: 'Network ID not available' } };
  }

  if (!deviceId) {
    console.error('SASE API: Cannot call checkGatewaySubnet without deviceId');
    return { response: { status: false, message: 'Device ID not available' } };
  }

  try {
    const response = await axiosInstance.get(
      `${API_BASE_URL}saseDevice/v1/partner/organisation/gateway/subnet/${_Id}/${networkId}/${deviceId}`,
      {
        possibleHeaders: ['_Id', 'networkId']
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error checking gateway subnet:', error);
    throw error;
  }
};

/**
 * Enable SASE Security at device level
 * URL: saseDevice/v1/partner/organisation/gateway/{_Id}/{networkId}/{deviceId}
 */
export const enableSaseSecurityDevice = async () => {
  const { _Id, networkId } = getCommonIds();
  const deviceId = localStorage.getItem('selectedDeviceId');

  if (!_Id) {
    console.error('SASE API: Cannot call enableSaseSecurityDevice without _Id');
    return { response: { status: false, message: 'User ID not available' } };
  }

  if (!networkId) {
    console.error('SASE API: Cannot call enableSaseSecurityDevice without networkId');
    return { response: { status: false, message: 'Network ID not available' } };
  }

  if (!deviceId) {
    console.error('SASE API: Cannot call enableSaseSecurityDevice without deviceId');
    return { response: { status: false, message: 'Device ID not available' } };
  }

  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}saseDevice/v1/partner/organisation/gateway/${_Id}/${networkId}/${deviceId}`,
      {},
      {
        headers: {
          networkId: networkId
        },
        possibleHeaders: ['_Id']
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error enabling device SASE security:', error);
    throw error;
  }
};

/**
 * Enable SASE Security at device level with SD-WAN disabled option
 * URL: saseDevice/v1/partner/organisation/gateway/{_Id}/{networkId}/{deviceId}/{isSDWanDisabled}
 * @param {boolean} isSDWanDisabled - Whether to disable SD-WAN
 */
export const enableSaseSecurityDeviceWithSDWan = async (isSDWanDisabled = false) => {
  const { _Id, networkId } = getCommonIds();
  const deviceId = localStorage.getItem('selectedDeviceId');

  if (!_Id) {
    console.error('SASE API: Cannot call enableSaseSecurityDeviceWithSDWan without _Id');
    return { response: { status: false, message: 'User ID not available' } };
  }

  if (!networkId) {
    console.error('SASE API: Cannot call enableSaseSecurityDeviceWithSDWan without networkId');
    return { response: { status: false, message: 'Network ID not available' } };
  }

  if (!deviceId) {
    console.error('SASE API: Cannot call enableSaseSecurityDeviceWithSDWan without deviceId');
    return { response: { status: false, message: 'Device ID not available' } };
  }

  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}saseDevice/v1/partner/organisation/gateway/${_Id}/${networkId}/${deviceId}/${isSDWanDisabled}`,
      {},
      {
        headers: {
          networkId: networkId
        },
        possibleHeaders: ['_Id']
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error enabling device SASE security with SD-WAN option:', error);
    throw error;
  }
};

/**
 * Disable SASE Security at device level
 * URL: saseDevice/v1/partner/organisation/gateway/{_Id}/{networkId}/{deviceId}
 */
export const disableSaseSecurityDevice = async () => {
  const { _Id, networkId } = getCommonIds();
  const deviceId = localStorage.getItem('selectedDeviceId');

  if (!_Id) {
    console.error('SASE API: Cannot call disableSaseSecurityDevice without _Id');
    return { response: { status: false, message: 'User ID not available' } };
  }

  if (!networkId) {
    console.error('SASE API: Cannot call disableSaseSecurityDevice without networkId');
    return { response: { status: false, message: 'Network ID not available' } };
  }

  if (!deviceId) {
    console.error('SASE API: Cannot call disableSaseSecurityDevice without deviceId');
    return { response: { status: false, message: 'Device ID not available' } };
  }

  try {
    const response = await axiosInstance.delete(
      `${API_BASE_URL}saseDevice/v1/partner/organisation/gateway/${_Id}/${networkId}/${deviceId}`,
      {
        possibleHeaders: ['_Id']
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error disabling device SASE security:', error);
    throw error;
  }
};

/**
 * Create partner org session at device level
 * Returns redirect URL for security console specific to device
 * URL: saseDevice/v1/createPartnerOrgGatewaySession/{_Id}/{networkId}/{deviceId}
 */
export const createPartnerOrgSessionDevice = async () => {
  const { _Id, networkId } = getCommonIds();
  const deviceId = localStorage.getItem('selectedDeviceId');

  if (!_Id) {
    console.error('SASE API: Cannot call createPartnerOrgSessionDevice without _Id');
    return { response: { status: false, message: 'User ID not available' } };
  }

  if (!networkId) {
    console.error('SASE API: Cannot call createPartnerOrgSessionDevice without networkId');
    return { response: { status: false, message: 'Network ID not available' } };
  }

  if (!deviceId) {
    console.error('SASE API: Cannot call createPartnerOrgSessionDevice without deviceId');
    return { response: { status: false, message: 'Device ID not available' } };
  }

  try {
    const response = await axiosInstance.post(
      `${API_BASE_URL}saseDevice/v1/createPartnerOrgGatewaySession/${_Id}/${networkId}/${deviceId}`,
      {},
      {
        possibleHeaders: ['_Id']
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating device partner org session:', error);
    throw error;
  }
};

/**
 * Delete partner org session at device level
 * URL: saseDevice/v1/deletePartnerOrgGatewaySession/{_Id}/{networkId}/{deviceId}
 */
export const deletePartnerOrgSessionDevice = async () => {
  const { _Id, networkId } = getCommonIds();
  const deviceId = localStorage.getItem('selectedDeviceId');

  if (!_Id) {
    console.error('SASE API: Cannot call deletePartnerOrgSessionDevice without _Id');
    return { response: { status: false, message: 'User ID not available' } };
  }

  if (!networkId) {
    console.error('SASE API: Cannot call deletePartnerOrgSessionDevice without networkId');
    return { response: { status: false, message: 'Network ID not available' } };
  }

  if (!deviceId) {
    console.error('SASE API: Cannot call deletePartnerOrgSessionDevice without deviceId');
    return { response: { status: false, message: 'Device ID not available' } };
  }

  try {
    const response = await axiosInstance.delete(
      `${API_BASE_URL}saseDevice/v1/deletePartnerOrgGatewaySession/${_Id}/${networkId}/${deviceId}`,
      {
        possibleHeaders: ['_Id']
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting device partner org session:', error);
    throw error;
  }
};

// ==============================|| SECURITY CONSOLE WINDOW MANAGER ||============================== //

/**
 * Global manager for security console windows
 * Tracks open windows and closes them on logout
 */
class SecurityConsoleManager {
  constructor() {
    this.windows = new Set();
  }

  registerWindow(windowRef) {
    if (windowRef) {
      this.windows.add(windowRef);
    }
  }

  unregisterWindow(windowRef) {
    this.windows.delete(windowRef);
  }

  closeAllWindows() {
    this.windows.forEach((windowRef) => {
      if (windowRef && !windowRef.closed) {
        windowRef.close();
      }
    });
    this.windows.clear();
  }

  hasOpenWindow() {
    // Clean up closed windows
    this.windows.forEach((windowRef) => {
      if (!windowRef || windowRef.closed) {
        this.windows.delete(windowRef);
      }
    });
    return this.windows.size > 0;
  }
}

export const securityConsoleManager = new SecurityConsoleManager();

/**
 * Open security console in new window via POST request
 * @param {string} redirUrl - The redirect URL for security console
 * @returns {Window|null} - Reference to the opened window or null if blocked
 */
export const openSecurityConsole = (redirUrl) => {
  if (!redirUrl) {
    console.error('No redirect URL provided for security console');
    return null;
  }

  const windowName = 'securityConsole_' + Date.now();

  // Create form for POST request
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = redirUrl;
  form.target = windowName;
  form.style.display = 'none';

  document.body.appendChild(form);

  // Open window with specific name before form submission
  const newWindow = window.open('about:blank', windowName);

  // Check if popup was blocked
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    document.body.removeChild(form);
    return null;
  }

  // Submit form immediately
  form.submit();

  // Clean up form
  document.body.removeChild(form);

  // Register window with global manager
  securityConsoleManager.registerWindow(newWindow);

  return newWindow;
};

// ==============================|| PUBLIC INFO APIs ||============================== //

/**
 * Get public application info (copyright, version, etc.)
 * URL: public/v2/info/{userId}
 * Uses useApi hook with GET_CLOUD_VERSION endpoint
 * @param {string} [overrideUserId] - Optional user ID to override the auto-detected one
 */
export const getPublicInfo = async (overrideUserId = null) => {
  const { _Id } = getCommonIds();
  const userId = overrideUserId || _Id;

  if (!userId) {
    return {
      response: {
        status: false,
        message: 'User ID not available'
      }
    };
  }

  try {
    const response = await apiService('GET_CLOUD_VERSION', {}, {}, ['_Id']);
    return response;
  } catch (error) {
    throw error;
  }
};

export default {
  // Organization level
  getPartnerUsage,
  enableSaseSecurity,
  disableSaseSecurity,
  createPartnerOrgSession,
  deletePartnerOrgSession,
  // Device level
  getPartnerUsageDeviceLevel,
  checkIpSecSites,
  checkGatewaySubnet,
  enableSaseSecurityDevice,
  enableSaseSecurityDeviceWithSDWan,
  disableSaseSecurityDevice,
  createPartnerOrgSessionDevice,
  deletePartnerOrgSessionDevice,
  // Public info
  getPublicInfo,
  // Utilities
  securityConsoleManager,
  openSecurityConsole
};